/* Warehouse TV terminal — /warehouse-tv?token=...
 *
 * Visual, minimum-text task display for warehouse pickers:
 *   - Huge product image cards with massive quantity numbers.
 *   - Realtime updates via SSE.
 *   - Sound: "ding" on every new task, "alarm" beeps for urgent/high.
 *   - Two-button workflow per task: Start (in_progress) + Done (completed).
 *   - No login: token from URL.
 *   - Fullscreen toggle + wake-lock.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
  tvListTasks, tvStartTask, tvDoneTask, tvStreamUrl, tvImageUrl,
} from "@/lib/warehouse";
import { CheckCircle2, Play, Volume2, VolumeX, Maximize2, Minimize2, AlertCircle, PackageCheck, Loader2 } from "lucide-react";

const PRIORITY_RING = {
  urgent: "ring-rose-500 shadow-rose-500/30 animate-[pulse_2s_ease-in-out_infinite]",
  high:   "ring-orange-500 shadow-orange-500/30",
  normal: "ring-slate-700/40",
  low:    "ring-slate-700/20",
};

const PRIORITY_LABEL = {
  urgent: { text: "URGENT", bg: "bg-rose-600" },
  high:   { text: "PRIORITAR", bg: "bg-orange-500" },
  normal: { text: "NORMAL", bg: "bg-slate-700" },
  low:    { text: "FĂRĂ PRIORITATE", bg: "bg-slate-500" },
};

export default function WarehouseTvPage() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [tasks, setTasks] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [muted, setMuted] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [connected, setConnected] = useState(false);
  const [now, setNow] = useState(Date.now());

  const audioCtxRef = useRef(null);
  const seenIdsRef = useRef(new Set());

  // ---- Audio synth ----
  const ensureAudio = useCallback(() => {
    if (audioCtxRef.current) return audioCtxRef.current;
    try {
      // eslint-disable-next-line no-undef
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return null;
      audioCtxRef.current = new Ctx();
      return audioCtxRef.current;
    } catch { return null; }
  }, []);

  const beep = useCallback((freq = 880, duration = 0.16, gain = 0.18) => {
    if (muted) return;
    const ctx = ensureAudio();
    if (!ctx) return;
    try {
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.type = "sine";
      o.frequency.value = freq;
      g.gain.value = 0.0001;
      o.connect(g); g.connect(ctx.destination);
      const t = ctx.currentTime;
      g.gain.exponentialRampToValueAtTime(gain, t + 0.01);
      g.gain.exponentialRampToValueAtTime(0.0001, t + duration);
      o.start(t);
      o.stop(t + duration + 0.02);
    } catch {}
  }, [muted, ensureAudio]);

  const playDing = useCallback(() => {
    // Pleasant 2-tone ding for normal new task.
    beep(880, 0.14, 0.2);
    setTimeout(() => beep(1320, 0.18, 0.18), 120);
  }, [beep]);

  const playUrgent = useCallback(() => {
    // 3 alarm beeps for urgent.
    [0, 220, 440].forEach((ms) => setTimeout(() => beep(1760, 0.13, 0.28), ms));
  }, [beep]);

  // ---- Initial load + SSE ----
  const reload = useCallback(async () => {
    if (!token) { setError("Token TV lipsește. Cere managerului linkul corect."); setLoading(false); return; }
    try {
      const data = await tvListTasks(token, 30);
      setError(null);
      const items = data.items || [];
      // First load: mark all as seen so we don't beep on initial render.
      if (seenIdsRef.current.size === 0) {
        items.forEach((t) => seenIdsRef.current.add(t.id));
      }
      setTasks(items);
    } catch (e) {
      setError(e.message || "Eroare la încărcare");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { reload(); }, [reload]);

  // SSE subscription
  useEffect(() => {
    if (!token) return;
    const es = new EventSource(tvStreamUrl(token));
    es.addEventListener("ready", () => setConnected(true));
    es.onerror = () => setConnected(false);
    es.onopen = () => setConnected(true);

    const onChange = () => { reload(); };
    es.addEventListener("warehouse.task_created", (e) => {
      try {
        const evt = JSON.parse(e.data);
        const d = evt?.data;
        if (d?.priority === "urgent" || d?.priority === "high") {
          playUrgent();
        } else {
          playDing();
        }
      } catch { playDing(); }
      reload();
    });
    es.addEventListener("warehouse.urgent_task", () => { /* sound already fired via task_created; just refresh */ reload(); });
    es.addEventListener("warehouse.task_updated", onChange);
    es.addEventListener("warehouse.task_completed", onChange);
    es.addEventListener("warehouse.task_deleted", onChange);

    return () => { try { es.close(); } catch {} };
  }, [token, reload, playDing, playUrgent]);

  // Tick every 30s for "X min ago" labels
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  // Wake lock + fullscreen change listener
  useEffect(() => {
    let wakeLock = null;
    if ("wakeLock" in navigator) {
      navigator.wakeLock.request("screen").then((wl) => { wakeLock = wl; }).catch(() => {});
    }
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => {
      try { wakeLock?.release(); } catch {}
      document.removeEventListener("fullscreenchange", onFs);
    };
  }, []);

  const onStart = async (task) => {
    try {
      ensureAudio(); // unlock audio on first interaction
      await tvStartTask(token, task.id);
      reload();
    } catch (e) { /* ignored */ }
  };
  const onDone = async (task) => {
    try {
      ensureAudio();
      beep(2200, 0.1, 0.18);
      setTimeout(() => beep(2640, 0.16, 0.22), 110);
      await tvDoneTask(token, task.id);
      reload();
    } catch (e) { /* ignored */ }
  };

  const toggleFullscreen = () => {
    try {
      if (!document.fullscreenElement) {
        document.documentElement.requestFullscreen();
      } else {
        document.exitFullscreen();
      }
    } catch {}
  };

  const toggleMute = () => {
    ensureAudio();
    setMuted((m) => !m);
  };

  // Sort: urgent first, then high, normal, low. Within group, oldest first.
  const sorted = useMemo(() => {
    const order = { urgent: 0, high: 1, normal: 2, low: 3 };
    return [...tasks].sort((a, b) => {
      const ra = order[a.priority] ?? 2;
      const rb = order[b.priority] ?? 2;
      if (ra !== rb) return ra - rb;
      return (a.created_at || "") < (b.created_at || "") ? -1 : 1;
    });
  }, [tasks]);

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-8" data-testid="warehouse-tv-error">
        <div className="text-center max-w-xl">
          <AlertCircle className="h-16 w-16 mx-auto text-rose-400 mb-4" />
          <h1 className="text-3xl font-bold mb-2">Eroare</h1>
          <p className="text-lg text-slate-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="warehouse-tv-page">
      {/* Top bar */}
      <div className="px-6 py-4 flex items-center justify-between border-b border-slate-800 bg-slate-900/80 backdrop-blur sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <PackageCheck className="h-7 w-7 text-emerald-400" />
          <h1 className="text-2xl font-bold tracking-tight">COLETARE</h1>
          <span className={`ml-2 flex items-center gap-1.5 text-xs px-2 py-1 rounded-full ${connected ? "bg-emerald-500/20 text-emerald-300" : "bg-rose-500/20 text-rose-300"}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${connected ? "bg-emerald-400 animate-pulse" : "bg-rose-400"}`} />
            {connected ? "LIVE" : "Offline"}
          </span>
        </div>

        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-xs text-slate-400 uppercase tracking-wide">Task-uri active</div>
            <div className="text-3xl font-bold tabular-nums" data-testid="warehouse-tv-count">{sorted.length}</div>
          </div>

          <button
            onClick={toggleMute}
            className="h-12 w-12 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition"
            data-testid="warehouse-tv-mute"
            title={muted ? "Activează sunetul" : "Dezactivează sunetul"}
          >
            {muted ? <VolumeX className="h-5 w-5" /> : <Volume2 className="h-5 w-5 text-emerald-400" />}
          </button>

          <button
            onClick={toggleFullscreen}
            className="h-12 w-12 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition"
            data-testid="warehouse-tv-fullscreen"
            title="Fullscreen"
          >
            {fullscreen ? <Minimize2 className="h-5 w-5" /> : <Maximize2 className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Task grid */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-32"><Loader2 className="h-12 w-12 animate-spin text-slate-500" /></div>
        ) : sorted.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 text-center">
            <CheckCircle2 className="h-24 w-24 text-emerald-500 mb-6" />
            <h2 className="text-4xl font-bold mb-2">Toate task-urile sunt finalizate!</h2>
            <p className="text-xl text-slate-400">Bună treabă! 🎉</p>
          </div>
        ) : (
          <div className="grid gap-5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(420px, 1fr))" }}>
            {sorted.map((t) => (
              <TaskCard key={t.id} task={t} token={token} now={now} onStart={onStart} onDone={onDone} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TaskCard({ task, token, now, onStart, onDone }) {
  const pri = PRIORITY_LABEL[task.priority] || PRIORITY_LABEL.normal;
  const ring = PRIORITY_RING[task.priority] || PRIORITY_RING.normal;
  const inProgress = task.status === "in_progress";
  const img = task.product?.image_url;
  const url = img ? tvImageUrl(img, token) : null;
  const ageMin = task.created_at ? Math.max(0, Math.round((now - new Date(task.created_at).getTime()) / 60000)) : 0;

  return (
    <div
      data-testid={`warehouse-tv-task-${task.id}`}
      className={`bg-slate-900 rounded-2xl overflow-hidden ring-2 shadow-2xl flex flex-col ${ring} ${
        inProgress ? "border-2 border-amber-500" : ""
      }`}
    >
      {/* Priority + age band */}
      <div className={`px-4 py-2 flex items-center justify-between text-sm font-bold ${pri.bg}`}>
        <span className="tracking-widest">{pri.text}</span>
        <span className="opacity-80 font-mono">{ageMin}m</span>
      </div>

      {/* Image */}
      <div className="relative bg-white">
        <div className="aspect-square w-full overflow-hidden flex items-center justify-center">
          {url ? (
            <img
              src={url}
              alt=""
              className="w-full h-full object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          ) : (
            <PackageCheck className="h-32 w-32 text-slate-300" />
          )}
        </div>
        {/* Quantity overlay */}
        <div className="absolute top-3 right-3 bg-black/85 text-white rounded-2xl px-5 py-3 flex flex-col items-center min-w-[100px]">
          <div className="text-[10px] uppercase tracking-widest text-slate-300">Cantitate</div>
          <div className="text-5xl font-bold tabular-nums leading-none">{task.quantity}</div>
        </div>
        {inProgress && (
          <div className="absolute bottom-3 left-3 bg-amber-500 text-black rounded-md px-3 py-1 text-xs font-bold flex items-center gap-1">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> ÎN COLECTARE
          </div>
        )}
      </div>

      {/* Title + notes */}
      <div className="p-4 flex-1 flex flex-col gap-2">
        <div className="text-lg font-semibold line-clamp-2 leading-snug">{task.product?.warehouse_label || task.product?.title || task.product_title_snapshot || "—"}</div>
        {task.notes && (
          <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-2 py-1 line-clamp-3">
            ⚠ {task.notes}
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div className="grid grid-cols-2 gap-1 p-3 pt-0">
        {!inProgress ? (
          <button
            data-testid={`warehouse-tv-start-${task.id}`}
            onClick={() => onStart(task)}
            className="h-16 rounded-xl bg-amber-500 hover:bg-amber-400 text-black text-xl font-bold flex items-center justify-center gap-2 transition active:scale-95"
          >
            <Play className="h-6 w-6" /> START
          </button>
        ) : (
          <div className="h-16 rounded-xl bg-amber-500/20 text-amber-300 text-base font-semibold flex items-center justify-center gap-2 border-2 border-amber-500/50">
            <Loader2 className="h-5 w-5 animate-spin" /> În lucru
          </div>
        )}
        <button
          data-testid={`warehouse-tv-done-${task.id}`}
          onClick={() => onDone(task)}
          className="h-16 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-black text-xl font-bold flex items-center justify-center gap-2 transition active:scale-95"
        >
          <CheckCircle2 className="h-6 w-6" /> DONE
        </button>
      </div>
    </div>
  );
}
